// src/utils/scheduleUtils.js

/**
 * Helper function to check if a date is a school holiday
 */
export const isSchoolHoliday = (dateString, schoolHolidays = []) => {
  if (!schoolHolidays || schoolHolidays.length === 0) return { isHoliday: false };
  
  const checkDate = new Date(dateString + 'T12:00:00');
  
  for (const holiday of schoolHolidays) {
    if (!holiday.enabled) continue;
    
    if (holiday.type === 'single' && holiday.date === dateString) {
      return { 
        isHoliday: true, 
        holidayName: holiday.name,
        holidayType: 'single'
      };
    }
    
    if (holiday.type === 'range' && holiday.startDate && holiday.endDate) {
      const holidayStart = new Date(holiday.startDate + 'T12:00:00');
      const holidayEnd = new Date(holiday.endDate + 'T12:00:00');
      
      if (checkDate >= holidayStart && checkDate <= holidayEnd) {
        return { 
          isHoliday: true, 
          holidayName: holiday.name,
          holidayType: 'range',
          startDate: holiday.startDate,
          endDate: holiday.endDate
        };
      }
    }
  }
  
  return { isHoliday: false };
};

/**
 * Determine schedule type for a given date
 * Handles rotating schedules, weekly patterns, holidays, and specific date overrides
 */
export const getScheduleTypeForDate = (dateString, config) => {
  const { scheduleTypes, calendarAssignments, schoolHolidays, systemConfig } = config;
  
  if (!scheduleTypes || !calendarAssignments) {
    return null;
  }

  const timezone = systemConfig?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Check if weekend
  const dateObj = new Date(dateString + 'T12:00:00');
  const dayOfWeek = dateObj.getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return {
      source: 'weekend',
      scheduleType: null,
      description: 'Weekend - No School'
    };
  }

  // Check if school holiday
  const holidayInfo = schoolHolidays?.find(holiday => {
    if (!holiday.enabled) return false;
    
    if (holiday.type === 'single' && holiday.date === dateString) {
      return true;
    }
    
    if (holiday.type === 'range' && holiday.startDate && holiday.endDate) {
      const holidayStart = new Date(holiday.startDate + 'T12:00:00');
      const holidayEnd = new Date(holiday.endDate + 'T12:00:00');
      const checkDate = new Date(dateString + 'T12:00:00');
      return checkDate >= holidayStart && checkDate <= holidayEnd;
    }
    
    return false;
  });

  if (holidayInfo) {
    return {
      source: 'holiday',
      scheduleType: null,
      description: `School Holiday: ${holidayInfo.name}`,
      holidayInfo: {
        holidayName: holidayInfo.name,
        holidayType: holidayInfo.type,
        startDate: holidayInfo.startDate,
        endDate: holidayInfo.endDate
      }
    };
  }

  // Check for specific date overrides
  const specificOverride = calendarAssignments.specificDates?.find(
    override => override.date === dateString
  );
  
  if (specificOverride) {
    const scheduleType = scheduleTypes.find(type => type.id === specificOverride.scheduleTypeId);
    return {
      source: 'specific',
      scheduleType,
      description: specificOverride.description
    };
  }

  // Check patterns
  for (const pattern of calendarAssignments.patterns || []) {
    // ✅ Handle TIME_BLOCKS pattern type (NEW!)
    if (pattern.type === 'time_blocks' && pattern.timeBlocks) {
      for (const block of pattern.timeBlocks) {
        if (block.startDate && block.endDate) {
          const blockStart = new Date(block.startDate + 'T12:00:00');
          const blockEnd = new Date(block.endDate + 'T12:00:00');
          const checkDate = new Date(dateString + 'T12:00:00');
          
          if (checkDate >= blockStart && checkDate <= blockEnd) {
            let scheduleType = null;
            
            // Handle different block types
            if (block.blockType === 'simple' && block.scheduleTypeId) {
              scheduleType = scheduleTypes.find(type => type.id === block.scheduleTypeId);
            } else if (block.blockType === 'weekly') {
              const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
              const dayName = dayNames[dateObj.getDay()];
              if (block.scheduleMapping && block.scheduleMapping[dayName]) {
                scheduleType = scheduleTypes.find(type => type.id === block.scheduleMapping[dayName]);
              }
            } else if (block.blockType === 'rotating') {
              const rotationDay = getRotationDayNumber(
                dateString, 
                block.startDate, 
                block.rotationDays || 2, 
                timezone, 
                schoolHolidays
              );
              if (rotationDay !== null && block.scheduleMapping) {
                const mappingKey = `day_${rotationDay}`;
                if (block.scheduleMapping[mappingKey]) {
                  scheduleType = scheduleTypes.find(type => type.id === block.scheduleMapping[mappingKey]);
                }
              }
            }
            
            if (scheduleType) {
              return {
                source: 'time_block',
                scheduleType,
                description: `${pattern.name} - ${block.name}`,
                pattern: pattern.name,
                blockName: block.name
              };
            }
          }
        }
      }
    }
    
    // Weekly pattern
    if (pattern.type === 'weekly') {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = dayNames[dateObj.getDay()];
      
      if (pattern.scheduleMapping && pattern.scheduleMapping[dayName]) {
        const scheduleType = scheduleTypes.find(type => type.id === pattern.scheduleMapping[dayName]);
        if (scheduleType) {
          return {
            source: 'weekly',
            scheduleType,
            description: pattern.description,
            pattern: pattern.name
          };
        }
      }
    }
    
    // Rotating pattern
    if (pattern.type === 'rotating' && pattern.startDate) {
      const rotationDay = getRotationDayNumber(
        dateString,
        pattern.startDate,
        pattern.rotationDays || 2,
        timezone,
        schoolHolidays
      );
      
      if (rotationDay !== null && pattern.scheduleMapping) {
        const mappingKey = `day_${rotationDay}`;
        if (pattern.scheduleMapping[mappingKey]) {
          const scheduleType = scheduleTypes.find(type => type.id === pattern.scheduleMapping[mappingKey]);
          if (scheduleType) {
            return {
              source: 'rotating',
              scheduleType,
              description: pattern.description,
              pattern: pattern.name,
              rotationDay
            };
          }
        }
      }
    }
  }

  // Default schedule
  const defaultSchedule = scheduleTypes.find(type => type.isDefault);
  return {
    source: 'default',
    scheduleType: defaultSchedule,
    description: 'Default schedule'
  };
};

// Helper function for rotation day calculation
const getRotationDayNumber = (targetDate, rotationStartDate, rotationDays, timezone = null, schoolHolidays = []) => {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  const target = new Date(targetDate + 'T12:00:00');
  const start = new Date(rotationStartDate + 'T12:00:00');
  
  if (target < start) return null;
  
  // Check if target is weekend
  const dayOfWeek = target.getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) return null;
  
  // Check if target is holiday
  const isHoliday = schoolHolidays?.some(holiday => {
    if (!holiday.enabled) return false;
    
    if (holiday.type === 'single' && holiday.date === targetDate) {
      return true;
    }
    
    if (holiday.type === 'range' && holiday.startDate && holiday.endDate) {
      const holidayStart = new Date(holiday.startDate + 'T12:00:00');
      const holidayEnd = new Date(holiday.endDate + 'T12:00:00');
      const checkDate = new Date(targetDate + 'T12:00:00');
      return checkDate >= holidayStart && checkDate <= holidayEnd;
    }
    
    return false;
  });
  
  if (isHoliday) return null;
  
  // Count weekdays (Monday-Friday) from start to target
  let weekdaysFromStart = 0;
  let currentDate = new Date(start);
  
  while (currentDate < target) {
    const dayOfWeek = currentDate.getDay();
    // Only count Monday (1) through Friday (5)
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      // Check if this day is not a holiday
      const currentDateString = currentDate.toISOString().split('T')[0];
      const isCurrentDayHoliday = schoolHolidays?.some(holiday => {
        if (!holiday.enabled) return false;
        
        if (holiday.type === 'single' && holiday.date === currentDateString) {
          return true;
        }
        
        if (holiday.type === 'range' && holiday.startDate && holiday.endDate) {
          const holidayStart = new Date(holiday.startDate + 'T12:00:00');
          const holidayEnd = new Date(holiday.endDate + 'T12:00:00');
          return currentDate >= holidayStart && currentDate <= holidayEnd;
        }
        
        return false;
      });
      
      if (!isCurrentDayHoliday) {
        weekdaysFromStart++;
      }
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Calculate which day in the rotation (1-based)
  return (weekdaysFromStart % rotationDays) + 1;
};

/**
 * Get period times for a specific period within a schedule type
 */
export const getPeriodTimesFromSchedule = (scheduleInfo, periodId) => {
  if (!scheduleInfo?.scheduleType || !periodId) {
    return { startTime: '08:00', endTime: '15:30' };
  }

  const periods = scheduleInfo.scheduleType.periods || [];
  const foundPeriod = periods.find(p => 
    p.id === periodId || 
    p.name === periodId ||
    p.id === String(periodId) ||
    p.name === String(periodId)
  );

  if (foundPeriod) {
    return {
      startTime: foundPeriod.start_time || '08:00',
      endTime: foundPeriod.end_time || '15:30'
    };
  }

  console.warn(`Period ${periodId} not found in schedule ${scheduleInfo.scheduleType.name}`);
  return { startTime: '08:00', endTime: '15:30' };
};